/**
 * FormThemeContext
 * React context that provides the active theme name ('dark' | 'light') to all
 * child controls, enabling theme-aware rendering without prop-drilling.
 */

import React, { createContext, useContext } from 'react';

export type FormTheme = 'dark' | 'light';

const FormThemeContext = createContext<FormTheme>('light');

export interface FormThemeProviderProps {
  theme: FormTheme;
  children: React.ReactNode;
}

export function FormThemeProvider({ theme, children }: FormThemeProviderProps) {
  return (
    <FormThemeContext.Provider value={theme}>
      {/* `display: contents` keeps this wrapper invisible to layout/flow — it exists only to
          give descendant CSS a real DOM hook for the app's own theme state. Without it, plain
          CSS files (e.g. form-section.css) can only key off the OS-level
          `@media (prefers-color-scheme: dark)`, which is a different, disconnected signal from
          this context — a real bug found live: FormSection's title rendered near-black
          (light-mode color) on the app's actual dark background whenever the OS didn't report
          dark mode, making section titles effectively invisible. */}
      <div data-af-theme={theme} style={{ display: 'contents' }}>
        {children}
      </div>
    </FormThemeContext.Provider>
  );
}

/**
 * Hook to read the current form theme from context.
 * Defaults to 'light' when used outside a provider.
 */
export function useFormTheme(): FormTheme {
  return useContext(FormThemeContext);
}

export { FormThemeContext };
